import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { statusColor } from "../components/ui/status-colors";
import {
  CheckCircle, XCircle, AlertTriangle, FileText, Loader2, Send, RefreshCw,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GuestReviewPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [action, setAction] = useState("");
  const [comments, setComments] = useState("");
  const [reviewerName, setReviewerName] = useState("");

  useEffect(() => {
    const fetchReview = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/guest-review/${token}`);
        setData(res.data);
        if (res.data.reviewer_name) setReviewerName(res.data.reviewer_name);
      } catch (err) {
        setError(err.response?.data?.detail || "Unable to load review. The link may be invalid or expired.");
      } finally {
        setLoading(false);
      }
    };
    fetchReview();
  }, [token]);

  const handleSubmit = async () => {
    if (!action) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/guest-review/${token}`, {
        action,
        comments,
        reviewer_name: reviewerName,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to submit review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center space-y-4">
            <AlertTriangle size={48} className="mx-auto text-amber-500" />
            <h2 className="text-lg font-semibold text-slate-800">Review Unavailable</h2>
            <p className="text-sm text-slate-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (data?.already_submitted || submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center space-y-4">
            <CheckCircle size={48} className="mx-auto text-emerald-500" />
            <h2 className="text-lg font-semibold text-slate-800">Review Submitted</h2>
            <p className="text-sm text-slate-500">
              {submitted
                ? "Thank you! Your feedback has been recorded successfully."
                : `Feedback was already submitted${data?.submitted_at ? ` on ${new Date(data.submitted_at).toLocaleDateString()}` : ""}.`}
            </p>
            {(data?.feedback_action || action) && (
              <Badge className={`text-sm ${
                statusColor((data?.feedback_action || action) === "approve" ? "approved" : (data?.feedback_action || action) === "reject" ? "rejected" : "pending")
              }`}>
                {(data?.feedback_action || action) === "request_changes" ? "Changes Requested" : (data?.feedback_action || action)}
              </Badge>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const actionButtons = [
    { key: "approve", label: "Approve", icon: CheckCircle, color: "bg-emerald-600 hover:bg-emerald-700 text-white", ring: "ring-emerald-300" },
    { key: "reject", label: "Reject", icon: XCircle, color: "bg-red-600 hover:bg-red-700 text-white", ring: "ring-red-300" },
    { key: "request_changes", label: "Request Changes", icon: RefreshCw, color: "bg-amber-600 hover:bg-amber-700 text-white", ring: "ring-amber-300" },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="guest-review-page">
      {/* Header */}
      <div className="bg-slate-900 text-white py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-indigo-500 rounded flex items-center justify-center">
              <FileText size={16} />
            </div>
            <span className="font-semibold tracking-tight">Contrack</span>
            <Badge className="bg-slate-700 text-slate-300 text-[10px] rounded-sm">EXTERNAL REVIEW</Badge>
          </div>
          <div className="text-sm text-slate-400">
            Reviewing as: <span className="text-white font-medium">{data.reviewer_name || data.reviewer_email}</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Document Info */}
        <Card data-testid="review-document-info">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl">{data.contract_title}</CardTitle>
              <Badge className="rounded-sm text-xs bg-indigo-100 text-indigo-700">{data.document_type === "notice" ? "Notice" : "Contract"}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              {data.contract_type && <InfoField label="Type" value={data.contract_type} />}
              {data.counterparty && <InfoField label="Counterparty" value={data.counterparty} />}
              {data.effective_date && <InfoField label="Effective" value={new Date(data.effective_date).toLocaleDateString()} />}
              {data.expiration_date && <InfoField label="Expires" value={new Date(data.expiration_date).toLocaleDateString()} />}
              {data.department && <InfoField label="Department" value={data.department} />}
              {data.jurisdiction && <InfoField label="Jurisdiction" value={data.jurisdiction} />}
              {data.contract_value && <InfoField label="Value" value={`$${Number(data.contract_value).toLocaleString()}`} />}
            </div>
          </CardContent>
        </Card>

        {/* Notice-specific Info */}
        {data.notice && (
          <Card data-testid="notice-info-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Notice Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {data.notice.subject && <InfoField label="Subject" value={data.notice.subject} />}
              {data.notice.notice_type && <InfoField label="Type" value={data.notice.notice_type} />}
              {data.notice.body && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">Body</p>
                  <div className="p-3 bg-slate-50 rounded border text-slate-700 whitespace-pre-wrap text-sm">{data.notice.body}</div>
                </div>
              )}
              {data.notice.required_action && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">Required Action</p>
                  <p className="text-slate-700">{data.notice.required_action}</p>
                </div>
              )}
              {data.notice.timeline && <InfoField label="Timeline" value={data.notice.timeline} />}
            </CardContent>
          </Card>
        )}

        {/* Contract Sections */}
        {data.sections?.length > 0 && (
          <Card data-testid="contract-sections">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Contract Sections</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {data.sections.map((s, i) => (
                <div key={i} className="border-b last:border-0 pb-4 last:pb-0">
                  <p className="text-sm font-semibold text-slate-700 mb-1">{s.name}</p>
                  <div className="text-sm text-slate-600 whitespace-pre-wrap">{s.content}</div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Fallback plain content */}
        {!data.sections?.length && data.content && (
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Contract Content</CardTitle></CardHeader>
            <CardContent>
              <div className="text-sm text-slate-600 whitespace-pre-wrap">{data.content}</div>
            </CardContent>
          </Card>
        )}

        {/* Review Action */}
        <Card className="border-2 border-indigo-200" data-testid="review-action-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Submit Your Review</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm font-medium text-slate-600 mb-2">Your Decision</p>
              <div className="flex flex-wrap gap-3">
                {actionButtons.map(ab => (
                  <Button
                    key={ab.key}
                    variant={action === ab.key ? "default" : "outline"}
                    className={`rounded-sm gap-2 ${action === ab.key ? ab.color + " " + ab.ring + " ring-2" : ""}`}
                    onClick={() => setAction(ab.key)}
                    data-testid={`review-action-${ab.key}`}
                  >
                    <ab.icon size={16} /> {ab.label}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-600 mb-2">Comments / Feedback</p>
              <Textarea
                placeholder="Provide your detailed feedback, concerns, or approval notes..."
                value={comments}
                onChange={e => setComments(e.target.value)}
                rows={4}
                className="rounded-sm"
                data-testid="review-comments"
              />
            </div>
            <div className="flex justify-end">
              <Button
                onClick={handleSubmit}
                disabled={!action || submitting}
                className="rounded-sm gap-2 bg-slate-900 hover:bg-slate-800"
                data-testid="submit-review-btn"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                Submit Review
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 py-4">
          Powered by Contrack &mdash; Contract Lifecycle Management
        </p>
      </div>
    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className="text-slate-700">{value}</p>
    </div>
  );
}
